import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface Comment {
    id: string
    taskId: string
    workspaceId: string
    authorId: string | null
    apiKeyName?: string
    content: unknown
    reactions: Array<{ userId: string; emoji: string }>
    createdAt: any
    updatedAt: any
}

interface CommentsState {
    byId: Record<string, Comment>
    allIds: string[]
    loading: boolean
    error: string | null
    // Which task's comments the store currently holds (set by the prefetch or the live listener),
    // so opening that task can reuse a prefetched thread instead of clearing + reloading.
    taskId: string | null
    // A live per-task listener is attached (a task modal is open) — the prefetch must not clobber it.
    listening: boolean
}

const initialState: CommentsState = {
    byId: {},
    allIds: [],
    loading: false,
    error: null,
    taskId: null,
    listening: false
}

const commentsSlice = createSlice({
    name: 'comments',
    initialState,
    reducers: {
        // Saga triggers
        startListening: (_state, _action: PayloadAction<{ taskId: string; workspaceId: string }>) => {},
        stopListening: () => {},
        createCommentSaga: (_state, _action: PayloadAction<{ taskId: string; workspaceId: string; authorId: string; content: unknown }>) => {},
        deleteCommentSaga: (_state, _action: PayloadAction<string>) => {},
        // One-shot prefetch of a task's comments (e.g. on hover for a task with commentCount > 0),
        // so opening it shows the thread immediately instead of after the listener's first snapshot.
        prefetchCommentsSaga: (_state, _action: PayloadAction<{ taskId: string; workspaceId: string }>) => {},

        // State mutations
        // Replace the store with one task's full comment list. The query is unbounded (all comments
        // for the task), so a full replace is always correct — there's no window to mistake an
        // evicted comment for a delete (SCD-925). onSnapshot latency-compensates the author's own
        // add/delete, so no optimistic insert/remove is needed.
        setCommentsForTask: (state, action: PayloadAction<{ taskId: string; comments: Comment[] }>) => {
            state.byId = {}
            state.allIds = []
            for (const comment of action.payload.comments) {
                state.byId[comment.id] = comment
                state.allIds.push(comment.id)
            }
            state.taskId = action.payload.taskId
        },
        setListening: (state, action: PayloadAction<boolean>) => {
            state.listening = action.payload
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },
        clearComments: (state) => {
            state.byId = {}
            state.allIds = []
            state.loading = false
            state.error = null
            state.taskId = null
            state.listening = false
        }
    }
})

export const {
    startListening,
    stopListening,
    createCommentSaga,
    deleteCommentSaga,
    prefetchCommentsSaga,
    setCommentsForTask,
    setListening,
    setLoading,
    setError,
    clearComments
} = commentsSlice.actions

export default commentsSlice.reducer
